import useDebouncedValue from "../hooks/useDebouncedValue";
import { useStateParams } from "../hooks/useStateParams";
import { SortOrder, isSortOrder } from "./common/models";
import { ApiPaginatedData } from "./common/paginatedData";
import useAuthenticatedFetch from "./common/useAuthenticatedFetch";
import { useAuthenticatedApiRequest } from "./common/useAuthenticatedFetcher";
import { ApiResponse } from "./models/ApiResponse";
import {
    AddOrganizationRequiredEvidenceDto,
    EmissionsSourceRequiredEvidenceOrderBy,
    RequiredEvidence,
    RequiredEvidenceForEmissionsSource,
    RequiredEvidenceStatusFilter,
    isEmissionsSourceRequiredEvidenceOrderBy,
} from "./models/RequiredEvidence";

export type useOrganizationRequiredEvidenceReturn = {
    requiredEvidence: RequiredEvidence[];
    isLoading: boolean;
    refetch: () => void;
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    evidenceTypeIds: number[];
    searchTerm: string;
    orderBy: EmissionsSourceRequiredEvidenceOrderBy;
    sortOrder: SortOrder;
    setPageNumber: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
    setEvidenceTypeIds: React.Dispatch<React.SetStateAction<number[]>>;
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
    setOrderBy: React.Dispatch<
        React.SetStateAction<EmissionsSourceRequiredEvidenceOrderBy>
    >;
    setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>;
};

export type useEmissionsSourceRequiredEvidenceReturn = {
    requiredEvidence: RequiredEvidenceForEmissionsSource[];
    isLoading: boolean;
    refetch: () => void;
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    evidenceTypeIds: number[];
    searchTerm: string;
    orderBy: EmissionsSourceRequiredEvidenceOrderBy;
    sortOrder: SortOrder;
    setPageNumber: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
    setEvidenceTypeIds: React.Dispatch<React.SetStateAction<number[]>>;
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
    setOrderBy: React.Dispatch<
        React.SetStateAction<EmissionsSourceRequiredEvidenceOrderBy>
    >;
    setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>;
};

export const defaultPageIndex = 0;
export const defaultPageSize = 15;
export const maxPageSize = 100;

export type useRequiredEvidenceForEmissionsSourcesOptions = {
    emissionsSourceId?: number;
    activityDataId?: number;
    initialPageSize?: number;
    ignoreFilteringAndSorting?: boolean;
};

export const useRequiredEvidenceForEmissionsSources = (
    statusFilter: RequiredEvidenceStatusFilter,
    options: useRequiredEvidenceForEmissionsSourcesOptions
): useEmissionsSourceRequiredEvidenceReturn => {
    const queryParamSuffix = "Ree";

    const [pageNumber, setPageNumber] = useStateParams<number>(
        defaultPageIndex,
        "page" + queryParamSuffix,
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageIndex)
    );

    const [pageSize, setPageSize] = useStateParams<number>(
        options.initialPageSize ?? defaultPageSize,
        "pageSize" + queryParamSuffix,
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageSize)
    );

    const [evidenceTypeIds, setEvidenceTypeIds] = useStateParams<number[]>(
        [],
        "evidenceTypeIds" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );

    const [searchTerm, setSearchTerm] = useStateParams<string>(
        "",
        "search" + queryParamSuffix,
        (s) => s,
        (s) => s
    );

    const [orderBy, setOrderBy] =
        useStateParams<EmissionsSourceRequiredEvidenceOrderBy>(
            "EvidenceTypeName",
            "orderBy" + queryParamSuffix,
            (s) => s,
            (s) =>
                isEmissionsSourceRequiredEvidenceOrderBy(s)
                    ? s
                    : "EvidenceTypeName"
        );
    const [sortOrder, setSortOrder] = useStateParams<SortOrder>(
        "asc",
        "sortOrder" + queryParamSuffix,
        (s) => s,
        (s) => (isSortOrder(s) ? s : "asc")
    );

    const debouncedSearchValue = useDebouncedValue(searchTerm, 500);

    let path =
        `api/required-emissions-source-evidence?statusFilter=${statusFilter}&pageIndex=${pageNumber}&pageSize=${pageSize}` +
        (options.emissionsSourceId
            ? `&emissionsSourceId=${options.emissionsSourceId}`
            : "") +
        (options.activityDataId
            ? `&activityDataId=${options.activityDataId}`
            : "");

    if (!options.ignoreFilteringAndSorting) {
        path =
            path +
            `&orderBy=${orderBy}` +
            `&sortOrder=${sortOrder}` +
            (evidenceTypeIds ? `&evidenceTypeIds=${evidenceTypeIds}` : "") +
            (searchTerm !== "" ? `&searchTerm=${debouncedSearchValue}` : "");
    }

    const { response, isLoading, refetch } =
        useAuthenticatedFetch<
            ApiResponse<ApiPaginatedData<RequiredEvidenceForEmissionsSource>>
        >(path);

    return {
        requiredEvidence: response?.content.items ?? [],
        isLoading,
        refetch,
        pageNumber,
        pageSize,
        totalCount: response?.content.totalCount ?? 0,
        totalPages: response?.content.totalPages ?? 0,
        hasPreviousPage: response?.content.hasPreviousPage ?? false,
        hasNextPage: response?.content.hasNextPage ?? false,
        evidenceTypeIds,
        searchTerm,
        orderBy,
        sortOrder,
        setPageNumber,
        setPageSize,
        setEvidenceTypeIds,
        setSearchTerm,
        setOrderBy,
        setSortOrder,
    };
};

export const useRequiredEvidenceForOrganization = (
    statusFilter: RequiredEvidenceStatusFilter,
    initialPageSize?: number,
    ignoreFilteringAndSorting?: boolean
): useOrganizationRequiredEvidenceReturn => {
    const queryParamSuffix = "Reo";

    const [pageNumber, setPageNumber] = useStateParams<number>(
        defaultPageIndex,
        "page" + queryParamSuffix,
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageIndex)
    );
    const [pageSize, setPageSize] = useStateParams<number>(
        initialPageSize ?? defaultPageSize,
        "pageSize" + queryParamSuffix,
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageSize)
    );

    const [evidenceTypeIds, setEvidenceTypeIds] = useStateParams<number[]>(
        [],
        "evidenceTypeIds" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );

    const [searchTerm, setSearchTerm] = useStateParams<string>(
        "",
        "search" + queryParamSuffix,
        (s) => s,
        (s) => s
    );

    const [orderBy, setOrderBy] =
        useStateParams<EmissionsSourceRequiredEvidenceOrderBy>(
            "EvidenceTypeName",
            "orderBy" + queryParamSuffix,
            (s) => s,
            (s) =>
                isEmissionsSourceRequiredEvidenceOrderBy(s)
                    ? s
                    : "EvidenceTypeName"
        );

    const [sortOrder, setSortOrder] = useStateParams<SortOrder>(
        "asc",
        "sortOrder" + queryParamSuffix,
        (s) => s,
        (s) => (isSortOrder(s) ? s : "asc")
    );

    const debouncedSearchValue = useDebouncedValue(searchTerm, 500);

    let path = `api/required-organization-evidence?statusFilter=${statusFilter}&pageIndex=${pageNumber}&pageSize=${pageSize}`;

    if (!ignoreFilteringAndSorting) {
        path =
            path +
            `&orderBy=${orderBy}` +
            `&sortOrder=${sortOrder}` +
            (evidenceTypeIds ? `&evidenceTypeIds=${evidenceTypeIds}` : "") +
            (searchTerm !== "" ? `&searchTerm=${debouncedSearchValue}` : "");
    }

    const { response, isLoading, refetch } =
        useAuthenticatedFetch<ApiResponse<ApiPaginatedData<RequiredEvidence>>>(
            path
        );

    return {
        requiredEvidence: response?.content.items ?? [],
        isLoading,
        refetch,
        pageNumber,
        pageSize,
        evidenceTypeIds,
        totalCount: response?.content.totalCount ?? 0,
        totalPages: response?.content.totalPages ?? 0,
        hasPreviousPage: response?.content.hasPreviousPage ?? false,
        hasNextPage: response?.content.hasNextPage ?? false,
        searchTerm,
        orderBy,
        sortOrder,
        setPageNumber,
        setPageSize,
        setEvidenceTypeIds,
        setSearchTerm,
        setOrderBy,
        setSortOrder,
    };
};

export const useAddOrganizationRequiredEvidenceType = () =>
    useAuthenticatedApiRequest<AddOrganizationRequiredEvidenceDto, void>(
        "POST",
        "api/organization-required-evidence-types"
    );

export const useRemoveOrganizationRequiredEvidenceType = (
    evidenceTypeId: number
) =>
    useAuthenticatedApiRequest(
        "DELETE",
        `api/organization-required-evidence-types/${evidenceTypeId}`
    );
